import React from "react";
import ReportResultsTable from "../shared/ReportResultsTable";

function PolicyBookOfBusinessReportBuilder({
  carriers,
  carriersLoading,
  coverageTypes,
  coverageTypesLoading,
  agents,
  agentsLoading,
  filters,
  setFilters,
  reportResults,
  builtConfig,
  formatFieldName
}) {
  const updateFilter = (field, value, value2 = "", operator = "equals") => {
    setFilters((prev) => {
      const next = [...prev];
      const index = next.findIndex((item) => item.field === field);

      if (
        value === "" ||
        value == null ||
        (field === "effective_date" && !value && !value2)
      ) {
        if (index >= 0) next.splice(index, 1);
        return next;
      }

      let filter;

      if (field === "effective_date") {
        if (value && value2) {
          filter = {
            field,
            operator: "between",
            value,
            value2
          };
        } else if (value) {
          filter = {
            field,
            operator: "after",
            value,
            value2: ""
          };
        } else {
          filter = {
            field,
            operator: "before",
            value: value2,
            value2: ""
          };
        }
      } else {
        filter = {
          field,
          operator,
          value,
          value2: ""
        };
      }

      if (index >= 0) {
        next[index] = filter;
      } else {
        next.push(filter);
      }

      return next;
    });
  };

  const getFilterValue = (field) => {
    const found = filters.find((item) => item.field === field);

    if (field === "effective_date" && found?.operator === "before") {
      return "";
    }

    return found?.value || "";
  };

  const getFilterValue2 = (field) => {
    const found = filters.find((item) => item.field === field);

    if (!found) return "";

    if (found.operator === "before") return found.value || "";

    return found?.value2 || "";
  };

  const handleClearFilters = () => {
    setFilters([]);
  };

  const selectedColumns = reportResults?.selectedColumns || builtConfig.selectedColumns || [];
  const rows = reportResults?.rows || [];
  const subtitle = reportResults
    ? `${reportResults.rowCount} row${reportResults.rowCount === 1 ? "" : "s"}`
    : "Run the report to view policy book of business";

  const toolbar = (
    <div className="sales-toolbar-card" style={{ marginBottom: "14px" }}>
      <div
        style={{
          display: "flex",
          gap: "10px",
          flexWrap: "wrap",
          alignItems: "center"
        }}
      >
        <select
          value={getFilterValue("coverage_type_id")}
          onChange={(e) => updateFilter("coverage_type_id", e.target.value)}
          className="sales-search-input"
          disabled={coverageTypesLoading}
          style={{ maxWidth: "220px" }}
        >
          <option value="">All Coverage Types</option>
          {coverageTypes.map((coverageType) => (
            <option key={coverageType.id} value={coverageType.id}>
              {coverageType.name}
            </option>
          ))}
        </select>

        <select
          value={getFilterValue("carrier_id")}
          onChange={(e) => updateFilter("carrier_id", e.target.value)}
          className="sales-search-input"
          disabled={carriersLoading}
          style={{ maxWidth: "220px" }}
        >
          <option value="">All Carriers</option>
          {carriers.map((carrier) => (
            <option key={carrier.id} value={carrier.id}>
              {carrier.name || carrier.carrier_name}
            </option>
          ))}
        </select>

        <select
          value={getFilterValue("status")}
          onChange={(e) => updateFilter("status", e.target.value)}
          className="sales-search-input"
          style={{ maxWidth: "220px" }}
        >
          <option value="">All Statuses</option>
          <option value="Active">Active</option>
          <option value="Approved">Approved</option>
          <option value="Pending/Submitted">Pending/Submitted</option>
          <option value="Pending">Pending</option>
          <option value="Submitted">Submitted</option>
          <option value="Inactive">Inactive</option>
          <option value="Cancelled">Cancelled</option>
          <option value="Terminated">Terminated</option>
        </select>

        <select
          value={getFilterValue("aor_user_id")}
          onChange={(e) => updateFilter("aor_user_id", e.target.value)}
          className="sales-search-input"
          disabled={agentsLoading}
          style={{ maxWidth: "220px" }}
        >
          <option value="">All Agents</option>
          {agents.map((agent) => (
            <option key={agent.id} value={agent.id}>
              {agent.name}
            </option>
          ))}
        </select>

        <input
          type="date"
          className="sales-search-input"
          value={getFilterValue("effective_date")}
          onChange={(e) =>
            updateFilter(
              "effective_date",
              e.target.value,
              getFilterValue2("effective_date")
            )
          }
          style={{ maxWidth: "170px" }}
        />

        <input
          type="date"
          className="sales-search-input"
          value={getFilterValue2("effective_date")}
          onChange={(e) =>
            updateFilter(
              "effective_date",
              getFilterValue("effective_date"),
              e.target.value
            )
          }
          style={{ maxWidth: "170px" }}
        />

        <input
          type="text"
          className="sales-search-input"
          placeholder="State"
          value={getFilterValue("state")}
          onChange={(e) =>
            updateFilter("state", e.target.value.trim().toUpperCase())
          }
          style={{ maxWidth: "120px" }}
        />

        <input
          type="text"
          className="sales-search-input"
          placeholder="ZIP Code"
          value={getFilterValue("zip_code")}
          onChange={(e) => updateFilter("zip_code", e.target.value.trim())}
          style={{ maxWidth: "140px" }}
        />

        <input
          type="text"
          className="sales-search-input"
          placeholder="County"
          value={getFilterValue("county")}
          onChange={(e) => updateFilter("county", e.target.value.trim())}
          style={{ maxWidth: "170px" }}
        />

        <button
          type="button"
          className="sales-secondary-btn"
          onClick={handleClearFilters}
        >
          Clear Filters
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div className="sales-section-card">
        <div className="sales-section-head">
          <h2>Policy Book of Business Criteria</h2>
          <p>
            Review policies by coverage type, carrier, status, effective date,
            location, and AOR agent.
          </p>
        </div>
      </div>

      <div style={{ marginTop: "10px" }}>
        <ReportResultsTable
          title="Report Results"
          subtitle={subtitle}
          selectedColumns={selectedColumns}
          rows={rows}
          emptyMessage={
            reportResults
              ? "No matching policies found."
              : "Run the report to view results."
          }
          toolbar={toolbar}
          formatFieldName={formatFieldName}
        />
      </div>
    </>
  );
}

export default PolicyBookOfBusinessReportBuilder;